import { useEffect, useRef, useState } from 'react';
import { useI18n } from './i18n';
import {
  isError,
  linkEmail,
  startEmailSignIn,
  startSignIn,
  verifyCode,
  verifyEmailCode,
} from './api';

/**
 * Sign in with a code, sent to a WhatsApp number or to an email address.
 *
 * Two steps on one screen rather than two screens, so the address stays visible while
 * the code is typed — the most common mistake is entering a code for an address you
 * mistyped, and being able to see it is what catches that. **The shape is unchanged by
 * adding email**, because the reason for it is unchanged: an email address is if anything
 * easier to mistype than a phone number, and a screen that has thrown the address away
 * cannot show anybody what they got wrong.
 *
 * The channel is a choice on the same screen rather than two routes, for the same
 * reason. Somebody who asked for a code on WhatsApp, waited, and wants to try email
 * instead should not lose the code field, the error they are reading, or their place.
 */
type Channel = 'phone' | 'email';
type Stage = 'address' | 'code' | 'done';

export default function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const { t } = useI18n();
  const [channel, setChannel] = useState<Channel>('phone');
  const [stage, setStage] = useState<Stage>('address');
  const [phone, setPhone] = useState('+506 ');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>();
  const codeRef = useRef<HTMLInputElement>(null);

  // Focus follows the step, so a phone keyboard does not have to be dismissed and the
  // field re-tapped between entering an address and entering a code.
  useEffect(() => {
    if (stage === 'code') codeRef.current?.focus();
  }, [stage]);

  async function requestCode(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);

    const result =
      channel === 'phone' ? await startSignIn(phone) : await startEmailSignIn(email);
    setBusy(false);

    if (isError(result)) {
      setProblem(
        result.error === 'invalid_number'
          ? t('signin.error.number')
          : result.error === 'invalid_email'
            ? t('signin.error.email')
            : result.error === 'rate_limited'
              ? t('signin.error.tooMany')
              : // A 503 here means the service has no sender configured, which is a
                // deploy that has not happened rather than anything the person typed.
                // Naming the other channel is the only useful thing to say about it.
                result.status === 503
                ? t('signin.error.unconfigured')
                : t('signin.error.generic'),
      );
      return;
    }
    setStage('code');
  }

  async function submitCode(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);

    const result =
      channel === 'phone' ? await verifyCode(phone, code) : await verifyEmailCode(email, code);
    setBusy(false);

    if (isError(result)) {
      setProblem(t('signin.error.code'));
      setCode('');
      codeRef.current?.focus();
      return;
    }
    setStage('done');
    onSignedIn();
  }

  if (stage === 'done') {
    return (
      <section className="panel">
        <h2>{t('signin.done.title')}</h2>
        <p>{t('signin.done.body')}</p>
      </section>
    );
  }

  return (
    <section className="panel">
      <h2>{t('signin.title')}</h2>

      <form onSubmit={stage === 'address' ? requestCode : submitCode} className="form">
        {/*
          A radio group rather than a tab strip or two buttons: it is one question with two
          answers, and `fieldset` is what says the two belong to one choice.

          A `<span>` for the question and not a `<legend>`, which is the shape every other
          fieldset in this app uses (`TargetFields` in `Objectives.tsx`) and not an
          oversight: `.field` is `display: grid`, and a `legend` is taken out of the grid
          flow by some browsers and not others, so the label lands somewhere different
          depending on who is looking at it.

          Disabled once a code is out, for the same reason the address field is: the code
          that arrived belongs to one channel, and switching underneath it would send a
          code's worth of typing at an address it was never issued for.
        */}
        <fieldset className="field" disabled={stage !== 'address' || busy}>
          <span>{t('signin.channel.legend')}</span>
          <div className="choices">
            {(['phone', 'email'] as const).map((option) => (
              <label key={option} className="choice">
                <input
                  type="radio"
                  name="channel"
                  value={option}
                  checked={channel === option}
                  onChange={() => {
                    setChannel(option);
                    setProblem(undefined);
                  }}
                />
                <span>
                  {t(option === 'phone' ? 'signin.channel.phone' : 'signin.channel.email')}
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        {channel === 'phone' ? (
          <label className="field">
            <span>{t('signin.number.label')}</span>
            <input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              name="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={stage !== 'address' || busy}
              required
            />
            <small>{t('signin.number.hint')}</small>
          </label>
        ) : (
          <label className="field">
            <span>{t('signin.email.label')}</span>
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              // Off, both of them. A phone that capitalises the first letter of every
              // field turns one address into two accounts as far as anybody typing can
              // tell — the API lowercases what it is given for exactly that reason, and
              // there is no sense in showing somebody a capital they did not intend.
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={stage !== 'address' || busy}
              required
            />
            <small>{t('signin.email.hint')}</small>
          </label>
        )}

        {stage === 'code' && (
          <label className="field">
            <span>{t('signin.code.label')}</span>
            <input
              ref={codeRef}
              type="text"
              // `one-time-code` is what makes a phone offer the code from the
              // notification instead of making somebody switch apps to read it.
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              name="code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
              disabled={busy}
              required
            />
            <small>{t('signin.code.hint')}</small>
          </label>
        )}

        {problem && (
          <p className="problem" role="alert">
            {problem}
          </p>
        )}

        <div className="actions">
          <button type="submit" className="button primary" disabled={busy}>
            {busy
              ? t('signin.working')
              : stage === 'address'
                ? t('signin.number.submit')
                : t('signin.code.submit')}
          </button>

          {stage === 'code' && (
            <button
              type="button"
              className="button"
              disabled={busy}
              onClick={() => {
                setStage('address');
                setCode('');
                setProblem(undefined);
              }}
            >
              {t(channel === 'phone' ? 'signin.code.back' : 'signin.email.back')}
            </button>
          )}
        </div>
      </form>
    </section>
  );
}

/**
 * Adding an email address to an account that already exists (F-101).
 *
 * In this file rather than its own because it is the same two steps as signing in —
 * prove an address, then spend the code — and the interesting part is what it is *not*
 * allowed to be. It calls `/api/auth/email/link`, which requires the session, and never
 * `/verify`: an endpoint that attached an address to whoever happened to be signed in
 * would mean a learner typing a second address to sign in with silently rewrote their
 * own account's, and one that attached it with no session at all would be a way to point
 * somebody else's account at an address you control.
 *
 * **It does not show the address the account already has**, and that is deliberate.
 * `/api/auth/me` answers out of the session cookie alone and reads no row, which is why
 * every page load costs nothing — showing the current address here would put a DynamoDB
 * read on that path for a line of text. The address is shown after it is added, which is
 * the moment somebody actually needs to see it confirmed.
 */
export function EmailAccess() {
  const { t } = useI18n();
  const [stage, setStage] = useState<'idle' | 'address' | 'code' | 'done'>('idle');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>();
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (stage === 'code') codeRef.current?.focus();
  }, [stage]);

  function reset() {
    setStage('idle');
    setEmail('');
    setCode('');
    setProblem(undefined);
  }

  async function requestCode(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);

    const result = await startEmailSignIn(email);
    setBusy(false);

    if (isError(result)) {
      setProblem(
        result.error === 'invalid_email'
          ? t('email.error.email')
          : result.error === 'rate_limited'
            ? t('email.error.tooMany')
            : t('email.error.generic'),
      );
      return;
    }
    setStage('code');
  }

  async function confirm(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);

    const result = await linkEmail(email, code);
    setBusy(false);

    if (isError(result)) {
      setProblem(
        // 409 is the one refusal worth its own sentence: the code was right and the
        // address belongs to another account, which is a different thing to do about it.
        result.error === 'in_use'
          ? t('email.error.inUse')
          : result.error === 'invalid_email'
            ? t('email.error.email')
            : t('email.error.code'),
      );
      setCode('');
      codeRef.current?.focus();
      return;
    }
    setStage('done');
  }

  if (stage === 'done') {
    return (
      <section className="panel">
        <h2>{t('email.title')}</h2>
        <p className="muted" role="status">
          {t('email.done').replace('{email}', email)}
        </p>
      </section>
    );
  }

  return (
    <section className="panel">
      <h2>{t('email.title')}</h2>
      <p className="muted">{t('email.body')}</p>

      {stage === 'idle' ? (
        <div className="actions">
          <button type="button" className="button" onClick={() => setStage('address')}>
            {t('email.submit')}
          </button>
        </div>
      ) : (
        <form onSubmit={stage === 'address' ? requestCode : confirm} className="form">
          <label className="field">
            <span>{t('email.label')}</span>
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={stage !== 'address' || busy}
              required
            />
          </label>

          {stage === 'code' && (
            <label className="field">
              <span>{t('email.code.label')}</span>
              <input
                ref={codeRef}
                type="text"
                autoComplete="one-time-code"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                name="code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                disabled={busy}
                required
              />
              <small>{t('email.code.hint')}</small>
            </label>
          )}

          {problem && (
            <p className="problem" role="alert">
              {problem}
            </p>
          )}

          <div className="actions">
            <button type="submit" className="button primary" disabled={busy}>
              {busy
                ? t('signin.working')
                : stage === 'address'
                  ? t('email.submit')
                  : t('email.confirm')}
            </button>
            <button type="button" className="button" disabled={busy} onClick={reset}>
              {t('email.cancel')}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
